import { useEffect, useRef, useState } from "react";
import "@/App.css";
import "@/aion-25d.css";
import "@/public-landing.css";
import { AuthScreen } from "@/components/aion/AuthScreen";
import { BootstrapAdminScreen } from "@/components/aion/BootstrapAdminScreen";
import { PublicLanding } from "@/components/aion/PublicLanding";
import { CustomerOwnerInviteScreen } from "@/components/aion/CustomerOwnerInviteScreen";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import { getCurrentUser, logoutAccount } from "@/services/api";

const SESSION_KEY = "aion-public-session";
const LEGACY_SESSION_KEY = "aion-admin-session";
const BUILD_REFRESH_PARAM = "__aion_build";

const sanitizeSession = (storedSession) => {
  if (!storedSession?.user) return null;
  return { token: null, user: storedSession.user };
};

const getInitialSession = () => {
  const stored = window.sessionStorage.getItem(SESSION_KEY) || window.localStorage.getItem(LEGACY_SESSION_KEY);
  if (!stored) return null;
  try {
    return sanitizeSession(JSON.parse(stored));
  } catch {
    window.sessionStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(LEGACY_SESSION_KEY);
    return null;
  }
};

const persistSession = (nextSession) => {
  const safeSession = sanitizeSession(nextSession);
  if (safeSession) {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify({ user: safeSession.user }));
  } else {
    window.sessionStorage.removeItem(SESSION_KEY);
  }
  window.localStorage.removeItem(LEGACY_SESSION_KEY);
};

function App() {
  const normalizedPath = window.location.pathname.replace(/\/+$/, "") || "/";
  const isCustomerOwnerInviteRoute = normalizedPath === "/invite/customer-owner";
  const isAdminSetupRoute = normalizedPath === "/admin-setup";
  const isPublicAuthLinkRoute = ["/auth/verify", "/auth/reset"].includes(normalizedPath);
  const publicPageByPath = {
    "/": "start",
    "/defaultsite": "start",
    "/features": "features",
    "/presence": "presence",
    "/how-it-works": "how",
  };
  const publicPage = publicPageByPath[normalizedPath] || null;
  const isAdminLoginRoute = normalizedPath === "/admin-login";
  const isCustomerLoginRoute = normalizedPath === "/login";
  const isLoginRoute = isCustomerLoginRoute || isAdminLoginRoute;
  const isRegisterRoute = normalizedPath === "/register";
  const [session, setSession] = useState(getInitialSession);
  const [booting, setBooting] = useState(true);
  const [authError, setAuthError] = useState("");
  const buildVersionRef = useRef(process.env.REACT_APP_BUILD_ID || "dev");

  useEffect(() => {
    if (isCustomerOwnerInviteRoute || isAdminSetupRoute || isPublicAuthLinkRoute || publicPage || isLoginRoute || isRegisterRoute) {
      setBooting(false);
      return undefined;
    }
    let active = true;
    const restore = async () => {
      try {
        const user = await getCurrentUser();
        if (!active) return;
        const restored = { token: null, user };
        setSession(restored);
        persistSession(restored);
      } catch {
        if (!active) return;
        window.sessionStorage.removeItem(SESSION_KEY);
      } finally {
        if (active) setBooting(false);
      }
    };
    void restore();
    return () => {
      active = false;
    };
  }, [isCustomerOwnerInviteRoute, isAdminSetupRoute, isPublicAuthLinkRoute, publicPage, isLoginRoute, isRegisterRoute]);

  useEffect(() => {
    if (isCustomerOwnerInviteRoute || isAdminSetupRoute || isPublicAuthLinkRoute || publicPage || isLoginRoute || isRegisterRoute) {
      return undefined;
    }

    let cancelled = false;

    const mountedUrl = new URL(window.location.href);
    if (mountedUrl.searchParams.get(BUILD_REFRESH_PARAM) === buildVersionRef.current) {
      mountedUrl.searchParams.delete(BUILD_REFRESH_PARAM);
      window.history.replaceState(
        {},
        document.title,
        mountedUrl.pathname + mountedUrl.search + mountedUrl.hash,
      );
    }

    const checkBuildVersion = async () => {
      try {
        const response = await fetch(`/aion-version.json?t=${Date.now()}`, {
          cache: "no-store",
          credentials: "same-origin",
        });
        if (!response.ok) return;
        const payload = await response.json();
        const nextBuild = String(payload?.build || "");
        if (!nextBuild || cancelled) return;
        if (buildVersionRef.current && buildVersionRef.current !== "dev" && nextBuild !== buildVersionRef.current) {
          const refreshUrl = new URL(window.location.href);
          if (refreshUrl.searchParams.get(BUILD_REFRESH_PARAM) === nextBuild) {
            // Safari/BFCache can restore an older JS runtime after a navigation.
            // Treat the marked build as already refreshed so this runtime cannot loop forever.
            buildVersionRef.current = nextBuild;
            return;
          }
          refreshUrl.searchParams.set(BUILD_REFRESH_PARAM, nextBuild);
          window.location.replace(refreshUrl.toString());
        }
      } catch {
        // Version polling is best-effort and must never block AION.
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") void checkBuildVersion();
    };
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void checkBuildVersion();
    }, 60000);

    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    void checkBuildVersion();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [isCustomerOwnerInviteRoute, isAdminSetupRoute, isPublicAuthLinkRoute, publicPage, isLoginRoute, isRegisterRoute]);

  const handleAuthenticated = async (response) => {
    const nextUser = response.user || (await getCurrentUser());
    const nextSession = { token: null, user: nextUser };
    persistSession(nextSession);
    if (isLoginRoute || isRegisterRoute || isPublicAuthLinkRoute || isAdminSetupRoute) {
      window.history.replaceState({}, document.title, "/app");
    }
    setSession(nextSession);
    setAuthError("");
  };

  const handleLogout = async (nextError = "") => {
    try {
      await logoutAccount(null);
    } catch {
      // Cookies may already be gone.
    }
    window.sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    setAuthError(nextError);
  };

  if (publicPage) {
    return (
      <>
        <PublicLanding page={publicPage} />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  if (isAdminSetupRoute) {
    return (
      <>
        <BootstrapAdminScreen onAuthenticated={handleAuthenticated} />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  if (isCustomerOwnerInviteRoute) {
    return (
      <>
        <CustomerOwnerInviteScreen />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  if (isPublicAuthLinkRoute || isLoginRoute || isRegisterRoute || !session) {
    return (
      <>
        <AuthScreen
          booting={booting}
          onAuthenticated={handleAuthenticated}
          initialError={authError}
          initialMode={isRegisterRoute ? "register" : undefined}
          accessPortal={isAdminLoginRoute ? "admin" : "customer"}
        />
        <Toaster richColors position="top-right" />
      </>
    );
  }

  return (
    <>
      <Dashboard token={null} initialUser={session.user} onLogout={handleLogout} />
      <Toaster richColors position="top-right" />
    </>
  );
}

export default App;
